import React from 'react'
import { Formik } from 'formik'
import { Columns } from 'react-bulma-components'
import useFilters from '../../hooks/filters'
import { FiltersResourcesEnum, PayrollFilters, PayrollWeekStatus } from '../../store/filters'
import {
  dateInputToDateValue,
  dateToDateInputValue,
  startOfWeek,
  formatISODate,
} from '../../utils/date'
import { addMonths, subMonths, isAfter, endOfWeek } from 'date-fns'
import AutoSubmit from '../form/utils/auto-submit'
import DateFilterField from '../filters/fields/date'
import FiltersWrapper from '../filters/filters-wrapper'
import ServiceFilterField from '../filters/fields/service'
import InternalWorkersFilterField from '../filters/fields/internal-workers'
import JobTitleFilterField from '../filters/fields/job-title'
import WeekStatusFilterField from '../filters/fields/week-status'

/**
 * Payroll filters component that allows filtering by user and date range
 * Uses the global filters store to maintain state across component re-renders
 */
const PayrollFilters: React.FC = () => {
  const { setFilters, filters } = useFilters(FiltersResourcesEnum.payroll)

  return (
    <Formik<
      Omit<PayrollFilters, 'start' | 'end'> & {
        start: string | undefined
        end: string | undefined
        weekStatuses: PayrollWeekStatus[]
      }
    >
      initialValues={{
        ...filters,
        start: filters.start ? dateToDateInputValue(new Date(filters.start)) : undefined,
        end: filters.end ? dateToDateInputValue(new Date(filters.end)) : undefined,
        weekStatuses: filters.weekStatuses || [],
      }}
      onSubmit={values => {
        setFilters(FiltersResourcesEnum.payroll, {
          ...values,
          start: values.start ? startOfWeek(dateInputToDateValue(values.start)) : undefined,
          end: values.end ? endOfWeek(dateInputToDateValue(values.end)) : undefined,
          weekStatuses: values.weekStatuses,
        })
      }}
      enableReinitialize
      validate={values => {
        const errors: Record<string, string> = {}
        if (!values.start) {
          errors.start = 'Champ requis'
        }
        if (!values.end) {
          errors.end = 'Champ requis'
        }
        // Validate that the selected range does not exceed 1 year and 1 month (13 months)
        if (values.start && values.end) {
          const start = dateInputToDateValue(values.start)
          const end = dateInputToDateValue(values.end)
          const maxEnd = addMonths(start, 13)
          const minStart = subMonths(end, 13)

          // End must be on/after start
          if (isAfter(start, end)) {
            errors.end = 'La date de fin doit être postérieure ou égale à la date de début'
          }

          // End must be within 13 months after start
          if (isAfter(end, maxEnd)) {
            errors.end = 'La plage ne peut pas dépasser 1 an et 1 mois'
          }

          // Start must be within 13 months before end
          if (isAfter(minStart, start)) {
            errors.start = 'La plage ne peut pas dépasser 1 an et 1 mois'
          }
        }
        return errors
      }}
    >
      {props => {
        return (
          <FiltersWrapper resource={FiltersResourcesEnum.payroll}>
            <Columns>
              <Columns.Column size={3} py={1}>
                <DateFilterField
                  name="start"
                  label="Date de début"
                  // Limit start to be not earlier than (end - 13 months) and not after end
                  min={
                    props.values.end
                      ? formatISODate(subMonths(dateInputToDateValue(props.values.end), 13))
                      : undefined
                  }
                  max={
                    props.values.end
                      ? formatISODate(dateInputToDateValue(props.values.end))
                      : undefined
                  }
                />
              </Columns.Column>
              <Columns.Column size={3} py={1}>
                <DateFilterField
                  name="end"
                  label="Date de fin"
                  min={
                    props.values.start
                      ? formatISODate(dateInputToDateValue(props.values.start))
                      : undefined
                  }
                  // Limit end to be not later than (start + 13 months)
                  max={
                    props.values.start
                      ? formatISODate(addMonths(dateInputToDateValue(props.values.start), 13))
                      : undefined
                  }
                />
              </Columns.Column>
              <Columns.Column size={3} py={1}>
                <WeekStatusFilterField name="weekStatuses" label="Statut des semaines" />
              </Columns.Column>
            </Columns>
            <Columns pb={1}>
              <Columns.Column size={3} py={1}>
                <InternalWorkersFilterField />
              </Columns.Column>
              <Columns.Column size={3} py={1}>
                <ServiceFilterField />
              </Columns.Column>
              <Columns.Column size={3} py={1}>
                <JobTitleFilterField />
              </Columns.Column>
            </Columns>
            <AutoSubmit values={props.values} submitForm={props.submitForm} />
          </FiltersWrapper>
        )
      }}
    </Formik>
  )
}

export default PayrollFilters
